import React, { useState, useEffect, useCallback } from 'react';

interface WealthRankingPlayer {
  name: string;
  berries: number;
  level: number;
}

interface WealthStats {
  totalPlayerPieces: number;
  avgPlayerPieces: number;
  medianPlayerPieces: number;
  topPlayers: WealthRankingPlayer[];
  wealthDistribution: {
    veryRich: number;
    rich: number;
    middle: number;
    poor: number;
    veryPoor: number;
  };
  giniCoefficient: number;
  topPlayerPercentage: number;
}

interface PlayerWealthMonitorProps {
  apiBaseUrl: string;
  adminKey: string;
}

export const PlayerWealthMonitor: React.FC<PlayerWealthMonitorProps> = ({ apiBaseUrl, adminKey }) => {
  const [wealthStats, setWealthStats] = useState<WealthStats | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);

  const fetchWealthStats = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/wealth-stats`, {
        headers: { 'x-admin-key': adminKey },
      });
      if (response.ok) {
        const data = await response.json();
        setWealthStats(data);
      }
    } catch (err) {
      console.error('Error fetching wealth stats:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, adminKey]);

  useEffect(() => {
    fetchWealthStats();
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchWealthStats(), 10000);
    return () => clearInterval(interval);
  }, [fetchWealthStats, autoRefresh]);

  if (!wealthStats) {
    return (
      <div style={{
        backgroundColor: '#1a1a2e',
        border: '1px solid #4a5568',
        borderRadius: 0,
        padding: 16,
        color: '#a0aec0',
        fontSize: 12,
        fontFamily: 'monospace',
      }}>
        LOADING WEALTH DATA...
      </div>
    );
  }

  const getWealthColor = (percentage: number): string => {
    if (percentage > 50) return '#ef4444';
    if (percentage > 30) return '#f59e0b';
    if (percentage > 20) return '#fbbf24';
    return '#22c55e';
  };

  const getGiniColor = (gini: number): string => {
    if (gini > 0.6) return '#ef4444';
    if (gini > 0.4) return '#f59e0b';
    return '#22c55e';
  };

  return (
    <div style={{
      backgroundColor: '#1a1a2e',
      border: '1px solid #4a5568',
      borderRadius: 0,
      padding: 16,
      marginBottom: 16,
      fontFamily: 'monospace',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <h3 style={{ color: '#ffa500', fontSize: 14, margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>PLAYER WEALTH MONITOR</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#a0aec0', fontSize: 11, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            AUTO-REFRESH
          </label>
          <button
            onClick={() => fetchWealthStats()}
            disabled={isLoading}
            style={{
              padding: '4px 8px',
              backgroundColor: isLoading ? '#2d3748' : '#4a5568',
              color: isLoading ? '#718096' : '#e2e8f0',
              border: '1px solid #4a5568',
              fontSize: 11,
              cursor: isLoading ? 'default' : 'pointer',
              borderRadius: 0,
            }}
          >
            {isLoading ? 'LOADING...' : 'REFRESH'}
          </button>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 12,
        marginBottom: 16,
      }}>
        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4 }}>TOTAL IN CIRCULATION</div>
          <div style={{ color: '#22c55e', fontSize: 16, fontWeight: 600 }}>
            ₱{wealthStats.totalPlayerPieces.toLocaleString()}
          </div>
        </div>

        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4 }}>AVERAGE PER PLAYER</div>
          <div style={{ color: '#3b82f6', fontSize: 16, fontWeight: 600 }}>
            ₱{Math.floor(wealthStats.avgPlayerPieces).toLocaleString()}
          </div>
        </div>

        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 4 }}>MEDIAN PER PLAYER</div>
          <div style={{ color: '#8b5cf6', fontSize: 16, fontWeight: 600 }}>
            ₱{Math.floor(wealthStats.medianPlayerPieces).toLocaleString()}
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 16,
        marginBottom: 16,
      }}>
        {/* Wealth Inequality Alerts */}
        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{ color: '#ffa500', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            INEQUALITY METRICS
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
              <span style={{ color: '#a0aec0' }}>Gini Coefficient:</span>
              <span style={{
                color: getGiniColor(wealthStats.giniCoefficient),
                fontWeight: 600,
              }}>
                {wealthStats.giniCoefficient.toFixed(2)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
              <span style={{ color: '#a0aec0' }}>Top Player %:</span>
              <span style={{
                color: getWealthColor(wealthStats.topPlayerPercentage),
                fontWeight: 600,
              }}>
                {wealthStats.topPlayerPercentage.toFixed(1)}%
              </span>
            </div>
            {wealthStats.topPlayerPercentage > 50 && (
              <div style={{
                padding: 8,
                backgroundColor: '#742a2a',
                borderRadius: 0,
                color: '#fed7d7',
                fontSize: 10,
              }}>
                TOP PLAYER HAS OVER 50% OF TOTAL WEALTH!
              </div>
            )}
            {wealthStats.giniCoefficient > 0.6 && (
              <div style={{
                padding: 8,
                backgroundColor: '#742a2a',
                borderRadius: 0,
                color: '#fed7d7',
                fontSize: 10,
              }}>
                HIGH WEALTH INEQUALITY DETECTED!
              </div>
            )}
          </div>
        </div>

        {/* Wealth Distribution */}
        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
        }}>
          <div style={{ color: '#ffa500', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            📊 DISTRIBUTION
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              { label: 'Very Rich', value: wealthStats.wealthDistribution.veryRich, color: '#ef4444' },
              { label: 'Rich', value: wealthStats.wealthDistribution.rich, color: '#f59e0b' },
              { label: 'Middle', value: wealthStats.wealthDistribution.middle, color: '#fbbf24' },
              { label: 'Poor', value: wealthStats.wealthDistribution.poor, color: '#3b82f6' },
              { label: 'Very Poor', value: wealthStats.wealthDistribution.veryPoor, color: '#a0aec0' },
            ].map(tier => (
              <div key={tier.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                <span style={{ color: '#a0aec0' }}>{tier.label}:</span>
                <span style={{ color: tier.color, fontWeight: 600 }}>{tier.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Players */}
      <div style={{
        backgroundColor: '#0b1220',
        border: '1px solid #4a5568',
        borderRadius: 0,
        padding: 12,
      }}>
        <div style={{ color: '#ffa500', fontSize: 12, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          🏆 TOP 10 RICHEST PLAYERS
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '30px 1fr 100px 80px',
          gap: 8,
          padding: '8px 0',
          borderBottom: '1px solid #4a5568',
          marginBottom: 8,
          fontSize: 10,
          color: '#a0aec0',
        }}>
          <div>#</div>
          <div>Name</div>
          <div>Pieces</div>
          <div>Level</div>
        </div>
        {wealthStats.topPlayers.slice(0, 10).map((player, idx) => (
          <div
            key={idx}
            style={{
              display: 'grid',
              gridTemplateColumns: '30px 1fr 100px 80px',
              gap: 8,
              padding: '8px 0',
              borderBottom: idx < 9 ? '1px solid #2d3748' : 'none',
              fontSize: 11,
              color: '#e2e8f0',
            }}
          >
            <div style={{ color: '#fbbf24', fontWeight: 600 }}>{idx + 1}</div>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {player.name}
            </div>
            <div style={{ color: '#22c55e', fontWeight: 600 }}>
              ₱{player.berries.toLocaleString()}
            </div>
            <div style={{ color: '#3b82f6' }}>Lv.{player.level}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
